
import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SeoProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  noIndex?: boolean;
}

const defaultImage = '/lovable-uploads/cd25ed71-78b3-45d5-baec-37bdcc2ad1a4.png';
const siteUrl = window.location.origin;

const Seo: React.FC<SeoProps> = ({
  title,
  description,
  keywords,
  image = defaultImage,
  url = window.location.pathname,
  type = 'website',
  publishedTime,
  modifiedTime,
  author,
  noIndex = false,
}) => {
  const siteTitle = 'Populism Immunity Framework';
  const fullTitle = title ? `${title} | ${siteTitle}` : siteTitle;
  const metaDescription = description || 'Strengthen civic resilience with the Populism Immunity Framework. Analyze populist rhetoric and develop critical thinking skills.';
  const metaKeywords = keywords || 'populism, rhetoric, critical thinking, democracy, civic resilience';
  const metaImage = image.startsWith('http') ? image : `${siteUrl}${image}`;
  const canonicalUrl = `${siteUrl}${url}`;

  return (
    <Helmet>
      {/* Basic Metadata */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={metaImage} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />
      
      {/* Article specific metadata */}
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === 'article' && author && (
        <meta property="article:author" content={author} />
      )}
      
      {/* Language tag */}
      <html lang="en" />
      
      {/* No index directive if needed */}
      {noIndex && <meta name="robots" content="noindex" />}
    </Helmet>
  );
};

export default Seo;
